import { FC, useEffect, useState } from "react";
import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { Box, CircularProgress, MenuItem, Typography } from "@mui/material";
import { formatCurrency, pxToRem } from "../../../util";
import { Colors } from "../../../configs";
import { RefundType } from "../../../typings/enum/RefundType";
import { CustomButton, CustomTextBox } from "../../../components";
import { CustomSelectBox } from "../../../components/atoms/custom-select-box/custom-select-box";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { paymentTypeLabels } from "../../../typings/enum/paymentTypes";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { useParams } from "react-router-dom";
import { refundInvoiceAction } from "../../../redux/actions/refundInvoiceAction";
import { IRefundInvoiceRequest } from "../../../typings/interfaces/requests/refund-invoice-request";
import { CartSliceAction } from "../../../redux/slice/cartSlice";

interface IRefundModalProps {
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
  approvedManagerId?: number;
}

export const RefundModal: FC<IRefundModalProps> = ({
  open,
  onClose,
  onRefundSuccess,
  approvedManagerId,
}) => {
  const [refundMethod, setRefundMethod] = useState<RefundType>(
    RefundType.STORE_CREDIT
  );
  const [reason, setReason] = useState<string>("");

  const { billPaymentDetails, cartHistorySelectedRows } = useAppSelector(
    (state) => state.cart
  );
  const { refundInvoiceLoading } = useAppSelector((state) => state.invoice);
  const { activeStation, activeStationCashier } = useAppSelector(
    (state) => state.cashierStation
  );
  const { user } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const params = useParams();

  const customerId = params["id"];
  const cashierId = user?.id;
  const hasSelection =
    !!cartHistorySelectedRows && cartHistorySelectedRows.length > 0;
  const canRefund =
    !!customerId &&
    !!cashierId &&
    !!activeStation?.cashierPoint?.id &&
    !!activeStationCashier?.stationAccessId &&
    hasSelection;

  useEffect(() => {
    if (open) {
      setReason("");
      setRefundMethod(RefundType.STORE_CREDIT);
    }
  }, [open]);

  // Surface why the modal can't proceed instead of failing silently, and
  // close it so the user isn't left looking at a blank/unresponsive modal.
  // Deliberately only re-runs on the open transition (not on every change
  // to hasSelection etc. while the modal stays open) — a successful refund
  // clears cartHistorySelectedRows as part of its own cleanup, and that
  // used to re-trigger this same effect and show a spurious "No invoice
  // selected" error right alongside the success message.
  useEffect(() => {
    if (!open) return;
    if (!customerId) {
      snackbar.showSnackbar("Customer not found", "error");
      onClose();
    } else if (!cashierId) {
      snackbar.showSnackbar("Cashier not found", "error");
      onClose();
    } else if (!activeStation?.cashierPoint?.id) {
      snackbar.showSnackbar("Station not found", "error");
      onClose();
    } else if (!activeStationCashier?.stationAccessId) {
      snackbar.showSnackbar("Station session not found", "error");
      onClose();
    } else if (!hasSelection) {
      snackbar.showSnackbar("No invoice selected", "error");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!canRefund) {
    return null;
  }

  const bill = cartHistorySelectedRows[0];
  const billId: number = bill?.id;
  const invoiceNumber: string = bill?.billId;

  const payments: {
    method: string;
    amount: number;
  }[] =
    (billPaymentDetails?.payments ?? []).length > 0
      ? billPaymentDetails?.payments
          .map((payment) => {
            // skip payments with paymentType LATER or MONTHLY
            if (
              payment.paymentType === "LATER" ||
              payment.paymentType === "MONTHLY"
            ) {
              return null;
            }

            return {
              method: paymentTypeLabels(payment.paymentType),
              amount: payment.amount,
            };
          })
          .filter(
            (payment): payment is { method: string; amount: number } =>
              payment !== null
          ) ?? []
      : [
          {
            method: "No Payments",
            amount: 0,
          },
        ];

  const handleClose = () => {
    setReason("");
    setRefundMethod(RefundType.STORE_CREDIT);
    onClose();
  };

  const handleRefundInvoice = () => {
    if (!approvedManagerId) {
      snackbar.showSnackbar(
        "Manager approval is required to process a refund.",
        "error"
      );
      return;
    }

    const refundPayload: IRefundInvoiceRequest = {
      cashierId: String(cashierId),
      refund: {
        refundItems: [
          {
            billId,
            refundMethod: refundMethod,
          },
        ],
        customerId: Number(customerId),
        cashierPointId: activeStationCashier.cashierPointId,
        stationAccessSessionId: activeStationCashier.stationAccessId as number,
        managerId: approvedManagerId,
        reason: reason,
      },
    };

    dispatch(refundInvoiceAction(refundPayload))
      .then((res) => {
        const payload = res.payload as
          | { status?: string; message?: string }
          | string
          | undefined;
        const status = typeof payload === "object" ? payload?.status : undefined;
        const message =
          typeof payload === "object" ? payload?.message : undefined;

        if (status === "SUCCESS") {
          dispatch(CartSliceAction.setRefreshCustomerHistory(true));
          // Deselect the just-refunded bill so it can't linger in
          // cartHistorySelectedRows and block Edit/Void/Refund for the
          // next bill the user tries to select.
          dispatch(CartSliceAction.setCartHistorySelectedRows([]));
          dispatch(CartSliceAction.setBillItemIdList([]));
          onRefundSuccess();
          handleClose();
          snackbar.showSnackbar("Refunded invoice successfully", "success");
        } else {
          snackbar.showSnackbar(
            message || "Failed to refund invoice. Please try again.",
            "error"
          );
        }
      })
      .catch(() => {
        snackbar.showSnackbar("Error refunding invoice", "error");
      });
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      title="Refund"
      width={pxToRem(550)}
      height={pxToRem(
        refundMethod === RefundType.STORE_CREDIT
          ? 512.868749925
          : payments.length === 1
          ? 512.868749925
          : payments.length === 2
          ? 538.868749925
          : payments.length === 3
          ? 568.86875
          : 588.868749925
      )}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: pxToRem(20),
        }}
      >
        <Box
          sx={{
            px: pxToRem(6),
            overflowY: `auto`,
            overflowX: `hidden`,
            pt: pxToRem(10),

            "&::-webkit-scrollbar": {
              width: "0.3rem",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ADADAD",
              borderRadius: "0rem",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
              borderRadius: "0rem",
            },
          }}
        >
          <Box
            sx={{
              padding: "0",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: pxToRem(12),
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexWrap: "nowrap",
                width: "100%",
                gap: pxToRem(25),
              }}
            >
              <Typography
                sx={{
                  fontSize: pxToRem(18),
                  color: Colors.MAIN_GRAY,
                  whiteSpace: "nowrap",
                  width: pxToRem(200),
                }}
              >
                Refund Method:
              </Typography>
              <CustomSelectBox
                value={refundMethod}
                test-name="refund method"
                onChange={(e) => setRefundMethod(e.target.value as RefundType)}
                width="100%"
                sx={{
                  "& .MuiSelect-icon": {
                    backgroundColor: Colors.SECONDARY,
                    color: Colors.WHITE,
                    width: "1.6rem",
                    height: "1.6rem",
                    fontSize: "1.2rem",
                    borderRadius: "0.4rem",
                    top: "calc(50% - 0.8rem)",
                  },
                }}
              >
                <MenuItem
                  test-name="Refund to Store Credits"
                  value={RefundType.STORE_CREDIT}
                >
                  Refund to Store Credits
                </MenuItem>
                <MenuItem
                  test-name="Refund to Same Method"
                  value={RefundType.SAME_METHOD}
                >
                  Refund to Same Method
                </MenuItem>
              </CustomSelectBox>
            </Box>

            {/* Store Credit Refund Amount */}
            {refundMethod === RefundType.STORE_CREDIT && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "nowrap",
                  width: "100%",
                  gap: pxToRem(25),
                }}
              >
                <Typography
                  sx={{
                    fontSize: pxToRem(18),
                    color: Colors.MAIN_GRAY,
                    whiteSpace: "nowrap",
                    width: pxToRem(200),
                  }}
                >
                  Store Credit Amount:
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    pl: pxToRem(7),
                  }}
                >
                  <CustomTextBox
                    value={formatCurrency({
                      amount: billPaymentDetails?.totalAmount ?? 0,
                      prefix: "$",
                    })}
                    disabled={true}
                    sx={{
                      width: `calc(100% - ${pxToRem(2)})`,
                      "& input": {
                        background: `${Colors.ACCENT_GRAY} !important`,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Refund Amounts */}
            {refundMethod === RefundType.SAME_METHOD && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  flexWrap: "nowrap",
                  width: "100%",
                  gap: pxToRem(25),
                }}
              >
                <Typography
                  sx={{
                    fontSize: pxToRem(18),
                    color: Colors.MAIN_GRAY,
                    whiteSpace: "nowrap",
                    width: pxToRem(200),
                  }}
                >
                  Refund Options
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    maxHeight: pxToRem(110),
                    paddingRight: payments.length > 3 ? pxToRem(10) : 0,
                  }}
                >
                  <ul
                    style={{
                      paddingInline: "0",
                      marginBlock: pxToRem(10),
                      paddingBlock: "0",
                      display: "flex",
                      flexDirection: "column",
                      gap: pxToRem(10),
                      fontSize: pxToRem(16),
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    {payments.map((payment, index) => (
                      <li
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginLeft: 0,
                          paddingLeft: 0,
                        }}
                      >
                        <span>{payment.method ?? "Undefined"}:</span>{" "}
                        <strong>
                          {formatCurrency({
                            amount: payment.amount,
                            prefix: "$",
                          })}
                        </strong>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "2rem",
            py: "1rem",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <Typography
              sx={{
                fontSize: pxToRem(18),
                color: Colors.TEXT_GRAY_DARK,
              }}
            >
              Reason for refunding invoice number{" "}
              <strong>{invoiceNumber}</strong>:
            </Typography>
            <CustomTextBox
              value={reason}
              test-name="refund text"
              onChange={(e) => setReason(e.target.value)}
              sx={{
                width: "100%",
                "& .MuiInputBase-input": {
                  width: pxToRem(467),
                  height: pxToRem(69),
                  minHeight: pxToRem(69),
                  maxHeight: pxToRem(69),
                },
              }}
            />
          </Box>

          <CustomButton
            sx={{
              background: Colors.PRIMARY,
              width: `100%`,
              height: pxToRem(64),
              fontSize: pxToRem(24),
              mt: pxToRem(40),
            }}
            onClick={handleRefundInvoice}
            test-name="refund button"
            disabled={
              reason.trim() === "" ||
              refundInvoiceLoading ||
              (billPaymentDetails?.payments ?? []).length === 0 ||
              (billPaymentDetails?.totalAmount ?? 0) === 0
            }
          >
            {refundInvoiceLoading ? (
              <CircularProgress
                size={18}
                sx={{ color: Colors.ACCENT_GRAY_DISABLED }}
              />
            ) : (
              "Refund Invoice"
            )}
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>
  );
};
